// ** React Imports
import { useContext, useMemo } from "react";

// ** Third Party Components
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

// ** Custom Components
import Text from "components/typography/Text";
import Link from "components/layout/Link";
import HeadingLink from "components/typography/HeadingLink";

// ** Utils & Helpers
import generateQueryPath from "utils/generateQueryPath";

// ** Context
import authContext from "contexts/authContext";

// ** Routes
import { initialQuery } from "containers/Users/initialQuery";
import Block from "components/layout/Block";

// #####################################################

const StyledLink = styled(Link)`
	display: inline-block;
	color: ${({ theme: { colors } }) => colors.black} !important;
`;

// #####################################################

const ConvertLocation = ({
	location,
	url,
	asLink,
	asHeroSectionLink,
	asHeroSectionLinkNew,
	asProfileLink,
	withoutRegion,
	asHeadingLink,
	preppendText,
	withQueryParameters,
}) => {
	const { city, cityId, region, regionId, country } = location || {};
	const { t } = useTranslation(["countries", "regions"]);
	const isAuth = useContext(authContext);

	let locationArray = [
		{
			type: "city",
			isset: (city && city.length > 0) || cityId,
			name: city,
			value: cityId,
		},
		{
			type: "region",
			isset: (region && region.length > 0) || regionId,
			name: regionId ? t(`regions:region_${regionId}`) : region,
			value: regionId,
		},
		{
			type: "country",
			isset: Boolean(country),
			name: country ? t(`countries:${country}`) : country,
			value: country,
		},
	].filter(({ isset }) => isset);

	if (withoutRegion) {
		locationArray = locationArray.filter(({ type }) => type !== "region");
	}

	const content = useMemo(
		() => ({
			linkable: locationArray
				.filter(({ value }) => Boolean(value) === true)
				.map(({ name }) => name)
				.join(", "),
			unlinkable: locationArray
				.filter(({ value }) => Boolean(value) === false)
				.map(({ name }) => name)
				.join(", "),
			all: locationArray.map(({ name }) => name).join(", "),
		}),
		[locationArray, city, cityId, region, regionId, country]
	);

	const cityLocation = locationArray.find(({ type }) => type === "city");
	const countryLocation = locationArray.find(
		({ type }) => type === "country"
	);

	const to = isAuth
		? generateQueryPath(url ? `/search/${url}` : `/search`, {
				...initialQuery,
				...withQueryParameters,
		  })
		: url
		? `/search/${url}`
		: `/search`;

	if (asLink === true) {
		return (
			<Text>
				{content.unlinkable.length > 0
					? `${content.unlinkable}, `
					: null}
				<Link underline to={to}>
					{content.linkable}
				</Link>
			</Text>
		);
	}

	if (asHeroSectionLink === true) {
		return (
			<Text color="whiteAlways">
				{content.unlinkable.length > 0
					? `${content.unlinkable}, `
					: null}
				<Link noColorVisited customColor="whiteAlways" to={to}>
					{content.linkable}
				</Link>
			</Text>
		);
	}

	if (asHeroSectionLinkNew === true && cityLocation?.name) {
		return (
			<Text color="whiteAlways">
				<Link
					noColorVisited
					underline
					customColor="whiteAlways"
					to={to}
				>
					{cityLocation?.name}
				</Link>
				, {countryLocation?.name}
			</Text>
		);
	}

	if (asProfileLink === true) {
		return (
			<Block my={2}>
				<StyledLink underline to={to} bold>
					<Text>
						{preppendText
							? `${preppendText} ${content.linkable}`
							: `${content.linkable}`}
					</Text>
				</StyledLink>
			</Block>
		);
	}

	if (asHeadingLink === true) {
		return (
			<HeadingLink to={to} type="h2" noPadding>
				{preppendText}{" "}
				{content.unlinkable.length > 0
					? `${content.unlinkable}, `
					: null}
				{content.linkable}
			</HeadingLink>
		);
	}

	return <Text>{content.all}</Text>;
};

// #####################################################

ConvertLocation.propTypes = {
	/**
	 * Lokalizacja jako link
	 * wyświetlana m.in. na profilu usera
	 * pod płci i wiekiem
	 */
	asLink: PropTypes.bool,
	/**
	 * Lokalizacja jako link typu heading (jak na stronie głównej)
	 */
	asHeadingLink: PropTypes.bool,
	/**
	 * Tekst dodawany przed linkiem typu 'HeadingLink'
	 */
	preppendText: PropTypes.string,
	/**
	 * Dodaj Query Parameters do linku
	 */
	withQueryParameters: PropTypes.object,
	/**
	 * Parametr określający czy region ma być wyświetlany
	 */
	withoutRegion: PropTypes.bool,
};

ConvertLocation.defaultProps = {
	asLink: false,
	asHeadingLink: false,
	preppendText: null,
	withQueryParameters: null,
	withoutRegion: false,
};

export default ConvertLocation;

// #####################################################
